import Handlebars from "handlebars";

type PostData = {
  content?: string | null;
  hashtags: {
    andTags: string[];
    first: string;
    all: string[];
    tags: string[];
  };
  embededLocation?: boolean;
  location?: string | null;
  savedDescriptions?: {
    start: string[];
    template: string[];
    end: string[];
  };
};

export type SavedDescriptionForPost = {
  id: number;
  slug: string;
  content: string;
  position: "START" | "INLINE" | "END" | "USE_TEMPLATE";
  selected: boolean;
  hashtags?: string[];
  hashtagsPosition?: "START" | "END" | "INLINE";
  tagsPosition?: "START" | "END" | "INLINE";
};

Handlebars.registerHelper("list", function (array, seperator, _context) {
  return array?.join?.(typeof seperator === "string" ? seperator : " ");
});

Handlebars.registerHelper("slice", function (array, start, end) {
  return array?.slice?.(start, end);
});

Handlebars.registerHelper("spaceIf", function (value) {
  const addSpace = isValidValue(value);
  return addSpace ? " " : "";
});

Handlebars.registerHelper("or", function (this: any, value1, value2, options) {
  return isValidValue(value1) || isValidValue(value2)
    ? options.fn(this)
    : options.inverse(this);
});

Handlebars.registerHelper("removeNewline", function (this: any, options) {
  const content = options.fn(this);
  if (content.trim() === "") {
    return "";
  }

  return content;
});

export function formatPostContent(
  template: string,
  data: PostData,
  savedDescriptions: SavedDescriptionForPost[] = []
) {
  if (!template || !data || !data.content) {
    return "";
  }

  console.log("Formatting post content with template:", template);
  console.log("Data being used:", data);
  console.log("Saved descriptions:", savedDescriptions);

  Handlebars.registerHelper(
    "savedDescription",
    function (slug: string, id: number | string) {
      const description = savedDescriptions.find((d: any) => d.id === id);
      return description
        ? description.content
        : `missing description for ${slug} & ${id}`;
    }
  );

  data.savedDescriptions = {
    start:
      savedDescriptions
        .filter((d) => d.position === "START" && d.selected)
        .map((d) => d.content) ?? [],
    template:
      savedDescriptions
        .filter((d) => d.position === "USE_TEMPLATE" && d.selected)
        .map((d) => d.content) ?? [],
    end:
      savedDescriptions
        .filter((d) => d.position === "END" && d.selected)
        .map((d) => d.content) ?? [],
  };

  if (!data.hashtags) {
    data.hashtags = {
      andTags: [],
      first: "",
      all: [],
      tags: [],
    };
  }

  // Store original user hashtags to preserve their order
  const originalHashtags = [...(data.hashtags.all || [])];
  const originalTags = [...(data.hashtags.tags || [])];
  const originalAndTags = [...(data.hashtags.andTags || [])];

  // Collect hashtags from saved descriptions while preserving their order
  const startHashtags: string[] = [];
  const startTags: string[] = [];
  const endHashtags: string[] = [];
  const endTags: string[] = [];

  // Process saved descriptions in their original order for START position
  for (const sd of savedDescriptions) {
    if (sd.hashtags && sd.hashtags.length > 0 && sd.selected) {
      const hashtags = sd.hashtags.filter((tag) => tag.startsWith("#"));
      const tags = sd.hashtags.filter((tag) => tag.startsWith("@"));

      if (sd.hashtagsPosition === "START") {
        // Add hashtags in their original order
        for (const tag of hashtags) {
          if (!startHashtags.includes(tag) && !originalHashtags.includes(tag)) {
            startHashtags.push(tag);
          }
        }
      }

      if (sd.tagsPosition === "START") {
        // Add tags in their original order
        for (const tag of tags) {
          if (!startTags.includes(tag) && !originalTags.includes(tag)) {
            startTags.push(tag);
          }
        }
      }

      if (sd.hashtagsPosition === "END") {
        // Add hashtags in their original order
        for (const tag of hashtags) {
          if (!endHashtags.includes(tag) && !originalHashtags.includes(tag)) {
            endHashtags.push(tag);
          }
        }
      }

      if (sd.tagsPosition === "END") {
        // Add tags in their original order
        for (const tag of tags) {
          if (!endTags.includes(tag) && !originalTags.includes(tag)) {
            endTags.push(tag);
          }
        }
      }
    }
  }

  // Combine in the correct order: START + original user hashtags + END
  data.hashtags.all = [...startHashtags, ...originalHashtags, ...endHashtags];
  data.hashtags.andTags = [
    ...startTags,
    ...startHashtags,
    ...originalAndTags,
    ...endHashtags,
    ...endTags,
  ];

  // Set first hashtag if not already set
  if (data.hashtags.all.length > 0) {
    data.hashtags.first = data.hashtags.all[0];
  }

  console.log("Data after processing hashtags:", data.hashtags);

  data.content = replaceInlineSavedDescriptions(
    data.content!,
    savedDescriptions
  );
  console.log("CONTENT AFTER INLINE REPLACEMENT:", data.content);

  const renderSavedDescriptions = Handlebars.compile(data.content, {
    noEscape: true,
  });
  data.content = renderSavedDescriptions(data);

  const render = Handlebars.compile(template, {
    noEscape: true,
  });
  const output = render(data);

  console.log("Formatted output:", output);

  return output.trim();
}

function isValidValue(value: any): boolean {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value !== null && value !== undefined && value !== "";
}

function replaceInlineSavedDescriptions(
  content: string,
  savedDescriptions: SavedDescriptionForPost[]
): string {
  if (!content || !savedDescriptions || savedDescriptions.length === 0) {
    return content;
  }

  const regex = /\+\[(?<slug>[\w\d\-\_]+)\]\((?<id>\d+)\)/g;

  console.log("REPLACE INLINE SAVED DESCRIPTIONS", content, savedDescriptions);

  return content.replace(regex, (match, slug, id) => {
    const description = savedDescriptions.find((d) => d.id === parseInt(id));
    console.log("REPLACE", match, slug, id, description);
    if (description) {
      return `{{savedDescription "${slug}" ${description.id}}}`;
    }
    return match;
  });
}
